import { mapStationDtoToRow } from '@/features/admin/utils/stationRow';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import { mapSensorTypeCodeToIoTType } from '@/features/realtime/utils/sensorTypeFromStationDto';
import {
  buildReadingsByChannelId,
  buildReadingsByRoutingKey,
  parseSensorTypeIdsFromDto,
  pickPrimaryBinding,
} from '@/features/realtime/utils/stationSensorBindings';

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function isoToMs(iso: unknown): number | undefined {
  if (typeof iso !== 'string' || !iso.trim()) return undefined;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : undefined;
}

/** Khóa catalog trên DTO: ưu tiên `sensor_type_id`, sau đó `sensor_type` (cùng metadata). */
export function stationDtoSensorTypeRaw(raw: Record<string, unknown>): string {
  return mapStationDtoToRow(raw).sensorTypes[0] ?? '';
}

/** Map DTO `GET /api/stations` → `IoTDataPoint` tối thiểu. */
export function mapStationDtoToIoTPoint(raw: Record<string, unknown>): IoTDataPoint {
  const row = mapStationDtoToRow(raw);
  const meta = row.metadata;
  const bindings = parseSensorTypeIdsFromDto(raw);
  const primaryBinding = pickPrimaryBinding(bindings);
  const sensorChannelIds = bindings.map((b) => b.id);
  const channelCodeById: Record<string, string> = {};
  for (const b of bindings) {
    if (b.sensor_type) channelCodeById[b.id] = b.sensor_type;
  }
  const readingsByChannelId = buildReadingsByChannelId(bindings);
  const readingsByRoutingKey = buildReadingsByRoutingKey(bindings);

  const routingFromBinding = primaryBinding?.sensor_type ?? null;
  const sensorTypeRaw =
    routingFromBinding ||
    (raw.sensor_type as string | undefined)?.trim() ||
    stationDtoSensorTypeRaw(raw);
  const type = sensorTypeRaw ? mapSensorTypeCodeToIoTType(sensorTypeRaw) : undefined;
  const sensorTypeName =
    primaryBinding?.sensor_type_name?.trim() ||
    (raw.sensor_type_name as string | undefined)?.trim() ||
    undefined;

  const sensorChannelCode = routingFromBinding || sensorTypeRaw || undefined;
  const sensorChannelId = primaryBinding?.id;
  const value =
    (primaryBinding?.id != null ? readingsByChannelId[primaryBinding.id] : undefined) ??
    num(meta.lastValue) ??
    num(meta.value) ??
    num(meta.latestValue) ??
    num((raw as { last_reading?: unknown }).last_reading) ??
    0;
  const lastReadingAtMs =
    isoToMs(primaryBinding?.last_reading_at) ??
    isoToMs((raw as { last_reading_at?: unknown }).last_reading_at) ??
    undefined;
  const now = Date.now();
  const stamp = lastReadingAtMs ?? now;
  const reportFromMeta =
    typeof meta.report_url === 'string'
      ? meta.report_url.trim()
      : typeof meta.reportUrl === 'string'
        ? meta.reportUrl.trim()
        : '';
  const reportFromRoot =
    typeof (raw as { report_url?: unknown }).report_url === 'string'
      ? String((raw as { report_url: string }).report_url).trim()
      : typeof (raw as { reportUrl?: unknown }).reportUrl === 'string'
        ? String((raw as { reportUrl: string }).reportUrl).trim()
        : '';
  const reportUrl = reportFromMeta || reportFromRoot || undefined;

  const base: IoTDataPoint = {
    id: row.id,
    ...(row.basinId?.trim() ? { basinId: row.basinId.trim() } : {}),
    ...(row.basinName?.trim() ? { basinName: row.basinName.trim() } : {}),
    sensorId: row.code?.trim() || row.id,
    name: row.name || row.code,
    position: [row.lon, row.lat],
    sensorChannelCode,
    ...(sensorChannelId ? { sensorChannelId } : {}),
    ...(sensorChannelIds.length > 0 ? { sensorChannelIds } : {}),
    ...(Object.keys(channelCodeById).length > 0 ? { channelCodeById } : {}),
    ...(Object.keys(readingsByChannelId).length > 0 ? { readingsByChannelId } : {}),
    ...(Object.keys(readingsByRoutingKey).length > 0 ? { readingsByRoutingKey } : {}),
    type,
    sensorTypeName,
    value,
    unit: typeof meta.unit === 'string' ? meta.unit : '',
    timestamp: stamp,
    lastUpdated: stamp,
    status: raw.operational_status === 'active' || raw.isActive === true ? 'online' : 'offline',
    reportUrl,
    specifications: {
      model: typeof meta.model === 'string' ? meta.model : '—',
      manufacturer: typeof meta.manufacturer === 'string' ? meta.manufacturer : '—',
      installYear: typeof meta.installYear === 'number' ? meta.installYear : 0,
    },
  };
  return base;
}
